import { useState, useEffect, useRef } from 'react'

export default function Demo() {
	const [activeTab, setActiveTab] = useState('Find & Filter')
	const [indicatorStyle, setIndicatorStyle] = useState({})
	const [tiltStyle, setTiltStyle] = useState({})
	const [imageLoaded, setImageLoaded] = useState({})
	const [isImageLoading, setIsImageLoading] = useState(false)
	const tabRefs = useRef({})
	const containerRef = useRef(null)
	const imageRef = useRef(null)

	const demoTabs = [
		{
			id: 'Find & Filter',
			label: 'Find & Filter',
			title: 'Find & Filter Loads',
			description:
				'Powerful auto-refresher with smart filters to help you find the right loads faster.',
			imageUrl:
				'https://loadfetcher-feature-images.nyc3.cdn.digitaloceanspaces.com/new-refresher.png',
		},
		{
			id: 'Book & Operate',
			label: 'Book & Operate',
			title: 'Book & Operate',
			description:
				'Streamline your booking process with quick order creation and automated post management.',
			imageUrl:
				'https://loadfetcher-feature-images.nyc3.cdn.digitaloceanspaces.com/post-truck.png',
		},
		{
			id: 'Track & Alert',
			label: 'Track & Alert',
			title: 'Track & Alert',
			description:
				'Real-time tracking of in-transit trips with proactive alerts for late deliveries and road restrictions.',
			imageUrl:
				'https://loadfetcher-feature-images.nyc3.cdn.digitaloceanspaces.com/tracking.png',
		},
		{
			id: 'Analyze',
			label: 'Analyze',
			title: 'Analyze Performance',
			description:
				'Comprehensive analytics to understand your load patterns, carrier performance, and market trends.',
			imageUrl:
				'https://loadfetcher-feature-images.nyc3.cdn.digitaloceanspaces.com/analyze.png',
		},
		{
			id: 'Finance',
			label: 'Finance',
			title: 'Finance & Statements',
			description:
				'Complete financial management with company and per-driver tracking, plus export-ready statements.',
			imageUrl:
				'https://loadfetcher-feature-images.nyc3.cdn.digitaloceanspaces.com/finance.png',
		},
	]

	const activeDemo = demoTabs.find(tab => tab.id === activeTab)

	// Preload all images
	useEffect(() => {
		const preloadImages = () => {
			demoTabs.forEach(tab => {
				const img = new Image()
				img.onload = () => {
					setImageLoaded(prev => ({ ...prev, [tab.id]: true }))
				}
				img.src = tab.imageUrl
			})
		}
		preloadImages()
	}, [])

	// Handle tab switching with loading state
	const handleTabChange = (tabId) => {
		if (!imageLoaded[tabId]) {
			setIsImageLoading(true)
		}
		setActiveTab(tabId)
	}

	// Reset loading state when image is ready
	useEffect(() => {
		if (imageLoaded[activeTab]) {
			setIsImageLoading(false)
		}
	}, [activeTab, imageLoaded])

	useEffect(() => {
		const updateIndicator = () => {
			const activeButton = tabRefs.current[activeTab]
			const container = containerRef.current

			if (activeButton && container) {
				const containerRect = container.getBoundingClientRect()
				const buttonRect = activeButton.getBoundingClientRect()

				setIndicatorStyle({
					left: buttonRect.left - containerRect.left,
					width: buttonRect.width,
					height: buttonRect.height,
				})
			}
		}

		updateIndicator()
		window.addEventListener('resize', updateIndicator)

		return () => window.removeEventListener('resize', updateIndicator)
	}, [activeTab])

	useEffect(() => {
		const handleMouseMove = e => {
			const image = imageRef.current
			if (!image) return

			const rect = image.getBoundingClientRect()
			const centerX = rect.left + rect.width / 2
			const centerY = rect.top + rect.height / 2

			const mouseX = e.clientX
			const mouseY = e.clientY

			const maxDistance = Math.max(window.innerWidth, window.innerHeight)
			const rotateX = ((mouseY - centerY) / maxDistance) * -15
			const rotateY = ((mouseX - centerX) / maxDistance) * 15

			setTiltStyle({
				transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`,
				transition: 'transform 0.1s ease-out',
			})
		}

		document.addEventListener('mousemove', handleMouseMove)

		return () => {
			document.removeEventListener('mousemove', handleMouseMove)
		}
	}, [])

	return (
		<div id="features" className="bg-white py-8 sm:py-10 pb-12 sm:pb-16">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="text-center mb-4 sm:mb-12 md:mb-8">
					<h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-700">
						Features at a glance
					</h2>
				</div>

				<div className="flex justify-center sm:mb-4">
					{/* Desktop Tabs */}
					<div className="hidden xl:flex items-center justify-center">
						<div
							className="bg-gray-100 p-1.5 rounded-full flex relative"
							ref={containerRef}
						>
							{/* Sliding indicator */}
							<div
								className="absolute bg-white rounded-full shadow-sm transition-all duration-300 ease-out"
								style={{
									left: indicatorStyle.left || 0,
									width: indicatorStyle.width || 0,
									height: indicatorStyle.height || 0,
									top: '6px',
								}}
							/>
							{demoTabs.map(tab => (
								<button
									key={tab.id}
									ref={el => (tabRefs.current[tab.id] = el)}
									onClick={() => handleTabChange(tab.id)}
									className={`relative z-10 px-6 py-3 rounded-full text-base font-medium transition-all duration-300 whitespace-nowrap ${
										activeTab === tab.id
											? 'text-gray-900'
											: 'text-gray-600 hover:text-gray-900'
									}`}
								>
									{tab.label}
								</button>
							))}
						</div>
					</div>

					{/* Mobile/Tablet Tabs */}
					<div className="xl:hidden w-full max-w-2xl px-2">
						<div className="bg-gray-100 p-1.5 rounded-full flex flex-wrap gap-1">
							{demoTabs.slice(0, 3).map(tab => (
								<button
									key={tab.id}
									onClick={() => handleTabChange(tab.id)}
									className={`flex-1 min-w-0 px-2 sm:px-3 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
										activeTab === tab.id
											? 'bg-white text-gray-900 shadow-sm'
											: 'text-gray-600 hover:text-gray-900'
									}`}
								>
									<span className="truncate">{tab.label}</span>
								</button>
							))}
						</div>
						<div className="bg-gray-100 p-1.5 rounded-full flex mt-2 gap-1">
							{demoTabs.slice(3).map(tab => (
								<button
									key={tab.id}
									onClick={() => handleTabChange(tab.id)}
									className={`flex-1 min-w-0 px-2 sm:px-3 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
										activeTab === tab.id
											? 'bg-white text-gray-900 shadow-sm'
											: 'text-gray-600 hover:text-gray-900'
									}`}
								>
									<span className="truncate">{tab.label}</span>
								</button>
							))}
						</div>
					</div>
				</div>

				<div className="max-w-4xl mx-auto space-y-4">
					<div className="text-center">
						<p className="text-sm sm:text-base lg:text-lg text-gray-800 leading-relaxed break-words">
							{activeDemo.description}
						</p>
					</div>

					<div className="relative">
						<div
							ref={imageRef}
							className="relative shadow-2xl bg-white rounded-lg overflow-hidden cursor-pointer"
							style={{
								...tiltStyle,
								boxShadow:
									'0 0 20px rgba(59, 130, 246, 0.4), 0 0 40px rgba(139, 92, 246, 0.3), 0 0 60px rgba(6, 182, 212, 0.2), 0 25px 50px -12px rgba(0, 0, 0, 0.25)',
							}}
						>
							<div className="relative" style={{ aspectRatio: '3022/1560' }}>
								{isImageLoading && (
									<div className="absolute inset-0 flex items-center justify-center bg-gray-50">
										<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
									</div>
								)}
								<img
									src={activeDemo.imageUrl}
									alt={`LoadFetcher ${activeDemo.title}`}
									className={`w-full h-full object-contain transition-opacity duration-300 ${
										isImageLoading ? 'opacity-0' : 'opacity-100'
									}`}
									loading="eager"
									decoding="async"
									onLoad={() => setImageLoaded(prev => ({ ...prev, [activeDemo.id]: true }))}
								/>
							</div>
						</div>

						<div className="absolute -inset-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-3xl blur-3xl opacity-30 -z-10"></div>
					</div>
				</div>
			</div>
		</div>
	)
}
